import React, { useCallback, useState } from 'react';
import { Container, Graphics, Text } from '@pixi/react';

const Circle = ({ shape, isSelected, onUpdate, onSelect, isBlueprintMode = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);

  const handleMouseDown = useCallback((event) => {
    if (!shape) return;
    
    event.stopPropagation();
    onSelect();
    
    const globalPoint = event.data?.global || event;
    setIsDragging(true);
    setDragStart({ 
      x: globalPoint.x - shape.x, 
      y: globalPoint.y - shape.y 
    });
  }, [shape, onSelect]);

  const handleMouseMove = useCallback((event) => {
    if (!isDragging || !dragStart) return;
    
    const globalPoint = event.data?.global || event;
    const newX = globalPoint.x - dragStart.x;
    const newY = globalPoint.y - dragStart.y;
    
    onUpdate({ x: newX, y: newY });
  }, [isDragging, dragStart, onUpdate]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  const drawCircle = useCallback((g) => {
    if (!shape) return;
    
    g.clear();
    
    // Use shape alpha for preview mode, default to 0.8 for normal mode
    const fillAlpha = shape.isPreview ? (shape.alpha || 0.5) : 0.8;
    const lineAlpha = shape.isPreview ? (shape.alpha || 0.5) : 1;
    
    // Draw main circle
    const strokeColor = isBlueprintMode ? 0xffffff : (isSelected ? 0xff6b6b : 0x333333);
    const fillColor = isBlueprintMode ? 0xffffff : (shape.color || 0x00cc99);
    g.lineStyle(isSelected ? 3 : 1, strokeColor, lineAlpha);
    g.beginFill(fillColor, fillAlpha);
    g.drawCircle(shape.radius, shape.radius, shape.radius);
    g.endFill();
  }, [shape, isSelected]);

  const drawNodes = useCallback((g) => {
    if (!shape || !isSelected) return;
    
    g.clear();
    
    // Draw center node
    g.beginFill(0x4ecdc4);
    g.drawCircle(shape.radius, shape.radius, 8);
    g.endFill();
    
    // Draw resize nodes at cardinal points
    const nodePositions = [
      { x: shape.radius, y: 0 }, // top
      { x: shape.radius * 2, y: shape.radius }, // right
      { x: shape.radius, y: shape.radius * 2 }, // bottom
      { x: 0, y: shape.radius } // left
    ];
    
    nodePositions.forEach((pos) => {
      g.beginFill(0xff6b6b);
      g.drawCircle(pos.x, pos.y, 6);
      g.endFill();
    });
  }, [shape, isSelected]);

  if (!shape) return null;

  return (
    <Container 
      x={shape.x} 
      y={shape.y}
      eventMode="static"
      onpointerdown={handleMouseDown}
      onpointermove={handleMouseMove}
      onpointerup={handleMouseUp}
    >
      {/* Main circle */}
      <Graphics draw={drawCircle} />
      
      {/* Render label if it exists */}
      {shape.label && (
        <Text
          text={shape.label}
          x={shape.radius}
          y={shape.radius}
          anchor={0.5}
          style={{
            fontFamily: 'Arial',
            fontSize: Math.min(shape.radius / 2, 16),
            fill: 0xffffff,
            align: 'center',
            fontWeight: 'bold',
            dropShadow: true,
            dropShadowColor: 0x000000,
            dropShadowBlur: 2,
            dropShadowAngle: Math.PI / 6,
            dropShadowDistance: 1,
          }}
        />
      )}
      
      {/* Control nodes */}
      {isSelected && (
        <Graphics draw={drawNodes} />
      )}
    </Container>
  );
};

export default Circle; 